import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Modal from '../Modal/Modal';
import translate from '../../libs/i18n';
import { colors } from '../../ui/variables';
import formatDate from '../../utils/formatDate';
import Api from '../../libs/Api';

const FeedbackListModalStyle = styled.div`
  .feedback-list-modal {
    max-height: 70vh;
    overflow-y: auto;
    padding: 10px;
    min-width: 600px;

    @media (max-width: 768px) {
      min-width: 100%;
      max-height: 80vh;
    }

    .modal-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 20px;
      color: ${colors.primaryDark};
    }

    .feedback-list {
      display: flex;
      flex-direction: column;
      gap: 15px;
    }

    .feedback-item {
      border: 1px solid ${colors.border || '#e0e0e0'};
      border-radius: 8px;
      padding: 15px;
      background-color: ${colors.background || '#f9f9f9'};
      display: flex;
      flex-direction: column;
      gap: 10px;

      .feedback-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;

        .feedback-info {
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;

          .feedback-user {
            font-weight: bold;
            color: ${colors.primaryDark};
          }

          .feedback-date {
            font-size: 12px;
            color: ${colors.textSecondary || '#999'};
          }
        }

        .feedback-actions {
          display: flex;
          gap: 8px;

          button {
            padding: 6px 12px;
            font-size: 12px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.2s;

            &.edit-btn {
              background-color: ${colors.primaryLight || '#e3f2fd'};
              color: ${colors.primary || '#1976d2'};

              &:hover {
                background-color: ${colors.primary || '#1976d2'};
                color: white;
              }
            }

            &.delete-btn {
              background-color: #ffebee;
              color: #d32f2f;

              &:hover {
                background-color: #d32f2f;
                color: white;
              }
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          }
        }
      }

      .feedback-justifications {
        background-color: white;
        padding: 10px;
        border-radius: 4px;
        border-left: 3px solid ${colors.primary || '#1976d2'};

        .justification-label {
          font-weight: bold;
          color: ${colors.primaryDark};
          margin-bottom: 8px;
          font-size: 12px;
        }

        .justification-list {
          display: flex;
          flex-direction: column;
          gap: 5px;

          .justification-item {
            font-size: 12px;
            color: ${colors.textSecondary || '#666'};
            padding-left: 15px;
            position: relative;

            &:before {
              content: '•';
              position: absolute;
              left: 0;
            }
          }
        }
      }

      .feedback-comments {
        background-color: white;
        padding: 10px;
        border-radius: 4px;
        border-left: 3px solid ${colors.success || '#4caf50'};

        .comments-label {
          font-weight: bold;
          color: ${colors.primaryDark};
          margin-bottom: 8px;
          font-size: 12px;
        }

        .comments-text {
          font-size: 12px;
          color: ${colors.textSecondary || '#666'};
          white-space: pre-wrap;
          word-break: break-word;
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      color: ${colors.textSecondary || '#999'};
      font-size: 14px;
    }

    .edit-form {
      display: flex;
      flex-direction: column;
      gap: 15px;

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        label {
          font-weight: bold;
          color: ${colors.primaryDark};
          font-size: 12px;
        }

        textarea {
          padding: 10px;
          border: 1px solid ${colors.border || '#e0e0e0'};
          border-radius: 4px;
          font-size: 12px;
          font-family: inherit;
          resize: vertical;
          min-height: 80px;
        }
      }

      .form-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;

        button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.2s;

          &.save-btn {
            background-color: ${colors.primary || '#1976d2'};
            color: white;

            &:hover {
              opacity: 0.9;
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          }

          &.cancel-btn {
            background-color: ${colors.border || '#e0e0e0'};
            color: ${colors.textSecondary || '#999'};

            &:hover {
              background-color: #d0d0d0;
            }
          }
        }
      }
    }
  }
`;

const FeedbackListModal = ({ reportId, feedbacks, onClose, currentUserId, isAdmin, onRefresh }) => {
  const language = useSelector((state) => state.settings.language);
  const [editingId, setEditingId] = useState(null);
  const [editComments, setEditComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canEditFeedback = (feedback) => isAdmin || feedback.user_id === currentUserId;

  const handleEditClick = (feedback) => {
    setEditingId(feedback.id);
    setEditComments(feedback.comments || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditComments('');
    setError(null);
  };

  const handleSaveEdit = async (feedbackId) => {
    setLoading(true);
    setError(null);
    try {
      const current = feedbacks.find((item) => item.id === feedbackId);
      if (!current) {
        throw new Error(translate('errorUpdatingFeedback', language));
      }

      const payload = {
        id: current.id,
        report: current.report,
        comments: editComments,
      };

      if (current.justifications && current.justifications.length) {
        payload.justifications = current.justifications.map((item) => item.id);
      }

      await Api.putReportFeedback(feedbackId, payload);

      setEditingId(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (feedbackId) => {
    if (window.confirm(translate('confirmDeleteFeedback', language))) {
      setLoading(true);
      setError(null);
      try {
        await Api.deleteReportFeedback(feedbackId);

        if (onRefresh) {
          onRefresh();
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <FeedbackListModalStyle>
      <Modal overlay="transparent" withShadow handleClose={onClose}>
        <div className="feedback-list-modal">
          <h3 className="modal-title">
            {translate('evaluations', language)}
          </h3>

          {error && (
            <div style={{ color: '#d32f2f', marginBottom: '15px', fontSize: '12px' }}>
              {error}
            </div>
          )}

          {feedbacks && feedbacks.length > 0 ? (
            <div className="feedback-list">
              {feedbacks.map((feedback) => (
                <div key={feedback.id} className="feedback-item">
                  {editingId === feedback.id ? (
                    <div className="edit-form">
                      <div className="form-group">
                        <label>{translate('comments', language)}</label>
                        <textarea
                          value={editComments}
                          onChange={(e) => setEditComments(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          className="cancel-btn"
                          onClick={handleCancelEdit}
                          disabled={loading}
                        >
                          {translate('cancel', language)}
                        </button>
                        <button
                          className="save-btn"
                          onClick={() => handleSaveEdit(feedback.id)}
                          disabled={loading}
                        >
                          {loading ? translate('saving', language) : translate('save', language)}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="feedback-header">
                        <div className="feedback-info">
                          <span className="feedback-user">
                            {feedback.user_name || translate('anonymous', language)}
                          </span>
                          <span className="feedback-date">
                            {formatDate(
                              feedback.date_created.replace(/-/g, ''),
                              language === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy'
                            )}
                          </span>
                        </div>
                        {canEditFeedback(feedback) && (
                          <div className="feedback-actions">
                            <button
                              className="edit-btn"
                              onClick={() => handleEditClick(feedback)}
                              disabled={loading}
                            >
                              {translate('edit', language)}
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteClick(feedback.id)}
                              disabled={loading}
                            >
                              {translate('delete', language)}
                            </button>
                          </div>
                        )}
                      </div>

                      {feedback.justifications && feedback.justifications.length > 0 && (
                        <div className="feedback-justifications">
                          <div className="justification-label">
                            {translate('justifications', language)}
                          </div>
                          <div className="justification-list">
                            {feedback.justifications.map((justification) => (
                              <div key={justification.id} className="justification-item">
                                {justification.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {feedback.comments && (
                        <div className="feedback-comments">
                          <div className="comments-label">
                            {translate('comments', language)}
                          </div>
                          <div className="comments-text">
                            {feedback.comments}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              {translate('noEvaluations', language)}
            </div>
          )}
        </div>
      </Modal>
    </FeedbackListModalStyle>
  );
};

FeedbackListModal.propTypes = {
  reportId: PropTypes.number.isRequired,
  feedbacks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      date_created: PropTypes.string.isRequired,
      comments: PropTypes.string,
      user_username: PropTypes.string,
      user_id: PropTypes.number,
      justifications: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  onClose: PropTypes.func.isRequired,
  currentUserId: PropTypes.number,
  isAdmin: PropTypes.bool,
  onRefresh: PropTypes.func,
};

FeedbackListModal.defaultProps = {
  feedbacks: [],
  currentUserId: null,
  isAdmin: false,
  onRefresh: null,
};

export default FeedbackListModal;
